import { useState, useRef } from 'react';
import financasApi from '@/lib/financasApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowUpRight, ArrowDownRight, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function FinancasImportar() {
  const [step, setStep] = useState(1); // 1 upload, 2 preview, 3 done
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [erros, setErros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incluidos, setIncluidos] = useState({});
  const [importados, setImportados] = useState(0);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = async () => {
      const conteudo = reader.result;
      setLoading(true);
      try {
        const { data } = await financasApi.post('/financas/importar/preview', { conteudo });
        setPreview(data.preview);
        setCategorias(data.categorias_disponiveis || []);
        setErros(data.erros || []);
        const inc = {};
        data.preview.forEach((p, i) => { inc[i] = true; });
        setIncluidos(inc);
        setStep(2);
        if (data.preview.length === 0) {
          toast.error('Nenhuma transação válida encontrada');
        } else {
          toast.success(`${data.preview.length} transações detectadas`);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Erro ao ler CSV');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const toggle = (i) => setIncluidos({ ...incluidos, [i]: !incluidos[i] });
  const toggleAll = () => {
    const anyOff = preview.some((_, i) => !incluidos[i]);
    const inc = {};
    preview.forEach((_, i) => { inc[i] = anyOff; });
    setIncluidos(inc);
  };

  const mudarCategoria = (i, catId) => {
    const cat = categorias.find(c => c.id === catId);
    const novo = [...preview];
    novo[i] = { ...novo[i], categoria_id: catId, categoria_nome: cat?.nome || 'Sem categoria' };
    setPreview(novo);
  };

  const mudarTipo = (i, tipo) => {
    const novo = [...preview];
    novo[i] = { ...novo[i], tipo };
    setPreview(novo);
  };

  const confirmar = async () => {
    const selecionadas = preview.filter((_, i) => incluidos[i]).map(p => ({
      tipo: p.tipo,
      valor: p.valor,
      categoria_id: p.categoria_id || null,
      categoria_nome: p.categoria_nome,
      descricao: p.descricao,
      data: p.data,
      forma_pagamento: p.forma_pagamento,
      status: p.status,
    }));
    if (selecionadas.length === 0) { toast.error('Selecione ao menos uma transação'); return; }
    setLoading(true);
    try {
      const { data } = await financasApi.post('/financas/importar/confirmar', { transacoes: selecionadas });
      setImportados(data.importadas);
      setStep(3);
      toast.success(`${data.importadas} transações importadas!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1); setPreview([]); setErros([]); setIncluidos({}); setFileName(''); setImportados(0);
    if (inputRef.current) inputRef.current.value = '';
  };

  const totalSelecionadas = Object.values(incluidos).filter(Boolean).length;
  const somaEntradas = preview.filter((p, i) => incluidos[i] && p.tipo === 'entrada').reduce((s, p) => s + p.valor, 0);
  const somaSaidas = preview.filter((p, i) => incluidos[i] && p.tipo === 'saida').reduce((s, p) => s + p.valor, 0);

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-importar-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <FileSpreadsheet className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Importar Extrato
        </h1>
        <p className="text-slate-400 text-xs md:text-sm mt-1">Cole ou envie o CSV do seu banco. Nós categorizamos automaticamente.</p>
      </div>

      {/* STEP 1 - Upload */}
      {step === 1 && (
        <div className="bg-slate-900 border border-white/10 rounded-xl p-8">
          <div
            className="border-2 border-dashed border-emerald-500/30 rounded-xl p-12 text-center cursor-pointer hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <p className="text-white text-lg font-medium mb-2">Arraste um CSV ou clique para selecionar</p>
            <p className="text-slate-400 text-sm">Formato esperado: <span className="text-emerald-400">data; descrição; valor</span> (separado por <span className="text-emerald-400">;</span> ou <span className="text-emerald-400">,</span>)</p>
            <p className="text-slate-500 text-xs mt-3">Valor negativo = saída • positivo = entrada • ou use colunas separadas "débito" / "crédito"</p>
            {loading && <p className="text-emerald-400 text-sm mt-4">Processando...</p>}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            className="hidden"
            data-testid="financas-import-file"
          />

          <div className="mt-6 bg-slate-950 border border-white/5 rounded-lg p-4">
            <p className="text-slate-400 text-xs mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-amber-400" /> Categorização automática</p>
            <p className="text-slate-500 text-xs">
              Reconhecemos automaticamente palavras como <span className="text-emerald-400">IFOOD, Posto Ipiranga, ENEL, Netflix, PIX Recebido, DAS, salário</span> e sugerimos a categoria correta. Você pode revisar e ajustar antes de salvar.
            </p>
          </div>
        </div>
      )}

      {/* STEP 2 - Preview */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="bg-slate-900 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-white text-sm font-medium">{fileName}</p>
                  <p className="text-slate-400 text-xs">{preview.length} linhas detectadas{erros.length > 0 ? `, ${erros.length} com erro` : ''}</p>
                </div>
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-slate-400">Selecionadas: <span className="text-white font-semibold">{totalSelecionadas}</span></span>
                <span className="text-emerald-400">Entradas: <span className="font-semibold">{fmtBRL(somaEntradas)}</span></span>
                <span className="text-red-400">Saídas: <span className="font-semibold">{fmtBRL(somaSaidas)}</span></span>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={reset} className="text-slate-400" data-testid="financas-import-cancelar">
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </Button>
                <Button onClick={confirmar} disabled={loading || totalSelecionadas === 0} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="financas-import-confirmar">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Importar {totalSelecionadas}
                </Button>
              </div>
            </div>
          </div>

          {erros.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-300 font-semibold flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> {erros.length} erro(s) no CSV</p>
              <ul className="text-amber-200/80 text-xs space-y-1">
                {erros.slice(0, 5).map((e, i) => <li key={`err-${i}`}>• {e}</li>)}
              </ul>
            </div>
          )}

          <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-800/50">
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wider">
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={totalSelecionadas === preview.length} onChange={toggleAll} className="accent-emerald-500" />
                  </th>
                  <th className="px-3 py-3">Data</th>
                  <th className="px-3 py-3">Descrição</th>
                  <th className="px-3 py-3">Tipo</th>
                  <th className="px-3 py-3">Categoria sugerida</th>
                  <th className="px-3 py-3 text-right">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {preview.map((p, i) => {
                  const isEnt = p.tipo === 'entrada';
                  return (
                    <tr key={`${p.data}-${p.descricao}-${p.valor}-${i}`} className={`${incluidos[i] ? '' : 'opacity-40'} hover:bg-white/5`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={!!incluidos[i]} onChange={() => toggle(i)} className="accent-emerald-500" data-testid={`financas-import-row-${i}`} />
                      </td>
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{p.data}</td>
                      <td className="px-3 py-2 text-white">{p.descricao}</td>
                      <td className="px-3 py-2">
                        <Select value={p.tipo} onValueChange={(v) => mudarTipo(i, v)}>
                          <SelectTrigger className="bg-slate-950 border-white/10 text-white h-8 w-24"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/10 text-white">
                            <SelectItem value="entrada"><span className="flex items-center gap-1 text-emerald-400"><ArrowUpRight className="w-3 h-3" />Entrada</span></SelectItem>
                            <SelectItem value="saida"><span className="flex items-center gap-1 text-red-400"><ArrowDownRight className="w-3 h-3" />Saída</span></SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Select value={p.categoria_id || ''} onValueChange={(v) => mudarCategoria(i, v)}>
                          <SelectTrigger className="bg-slate-950 border-white/10 text-white h-8 w-48"><SelectValue placeholder={p.categoria_nome} /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/10 text-white max-h-64">
                            {categorias
                              .filter(c => c.tipo === p.tipo || c.tipo === 'ambos')
                              .map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className={`px-3 py-2 text-right font-semibold ${isEnt ? 'text-emerald-400' : 'text-red-400'}`}>{fmtBRL(p.valor)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* STEP 3 - Done */}
      {step === 3 && (
        <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-10 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Importação concluída!</h2>
          <p className="text-slate-400 mb-6">{importados} transações foram adicionadas ao seu financeiro.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={reset} variant="ghost" className="text-slate-300 border border-white/10" data-testid="financas-import-novo">Importar outro CSV</Button>
            <Button onClick={() => window.location.href = '/financas/dashboard'} className="bg-emerald-600 hover:bg-emerald-500 text-white">Ver Dashboard</Button>
          </div>
        </div>
      )}
    </div>
  );
}
